import { reactive } from "vue";
import { useMediaQuery } from '@/composables/useMediaQuery';
import { useThrottleFn } from '@vueuse/core';

const isDesktop = useMediaQuery('(min-width: 840px)');

//TODO: make this a composable. Function name, file name, folder name
export let useSheetController = reactive({
  sheetOrder: {
    primary: [],
    secondary: []
  },
  sheets: {},
  showSheetBackdrop: false,

  doToggleSheet(type, content, keepOpen = false, history = false, details = {}) {
    const isOpen = this.sheets[type].open;
    const currContent = this.sheets[type].content;

    if (details) {
      this.sheets[type].details = details
    }

    if (isOpen && content === currContent && keepOpen) {
      this.bringToFront(type)
      this.updateBackdropStatus()
      return
    }

    if (isOpen && history && content !== currContent) {
      this.addSheetToHistory(type, currContent)
      this.sheets[type].content = content
      this.sheets[type].open = true
    } else if ((isOpen && content === currContent) || !content) {
      this.sheets[type].open = false
      this.sheets[type].size = ''
    } else {
      this.bringToFront(type)
      this.sheets[type].content = content
      this.sheets[type].open = true
    }

    if (!history) {
      this.sheets[type].history = []
      this.sheets[type].historyTransition = null
    }

    if (!isDesktop.value) {
      this.closeOtherSheets(type)
    }

    this.updateBackdropStatus()
  },

  // Leading-edge throttle: the first click acts immediately and rapid repeats
  // are ignored. A trailing debounce here delayed the open long enough that
  // users clicked again, toggling the sheet closed as it opened.
  toggleSheet: useThrottleFn((type, content, keepOpen = false, history = false, details = {}) => {
    useSheetController.doToggleSheet(type, content, keepOpen, history, details)
  }, 200, false),

  sendToBack(type) {
    const position = this.sheets[type].position
    this.sheetOrder[position] = [
      ...this.sheetOrder[position].filter((sheet) => sheet !== type),
      type
    ]
  },

  bringToFront(type) {
    const position = this.sheets[type].position
    this.sheetOrder[position] = [
      type,
      ...this.sheetOrder[position].filter((sheet) => sheet !== type)
    ]
  },

  closeSheet(type) {
    this.sheets[type].open = false
    this.sheets[type].size = ''

    this.updateBackdropStatus()
  },

  closeOtherSheets(type) {
    const sheets = Object.keys(this.sheets)

    sheets.forEach((sheet) => {
      if (sheet !== type) {
        this.sheets[sheet].open = false
      }
    })

    this.updateBackdropStatus()
  },

  closeAllSheets() {
    const sheets = Object.keys(this.sheets)

    sheets.forEach((sheet) => {
      this.sheets[sheet].open = false
    })

    this.updateBackdropStatus()
  },

  addSheetToHistory(type, content) {
    if (!this.sheets[type]?.history) {
      this.sheets[type].history = []
    }

    this.sheets[type].history.push(content)
    this.sheets[type].historyTransition = 'foward'
  },

  goBackInHistory(type) {
    const history = this.sheets[type].history

    if (history.length > 0) {
      this.sheets[type].historyTransition = 'back'
      this.doToggleSheet(type, history.pop())
    }
  },

  hasHistory(type) {
    return this.sheets[type]?.history?.length > 0
  },

  clearSheetTypeHistory(type) {
    if (this.sheets[type]?.history) {
      this.sheets[type].history = []
    }
  },

  updateBackdropStatus() {
    if (isDesktop.value) {
      return
    }

    const sheets = Object.keys(this.sheets)
    let showBackdrop = false

    sheets.forEach((sheet) => {
      if (this.sheets[sheet].size === 'maximized') {
        showBackdrop = true
      }
    })

    this.showSheetBackdrop = showBackdrop
  },

  registerSheet(type, position) {
    const sheet = {
      position,
      content: '',
      size: '',
      open: false
    }

    this.sheets[type] = sheet
    this.sheetOrder[position].push(type)
  }
})
